import { Abortable } from '../../lib/abortable.js';
import { i18n } from '../../lib/i18n/i18n.js';
import { notifyError, notifySuccess } from '../../lib/notifications.js';
import { isCellarExplorerErrorWithCode, pathToString } from '../cc-cellar-explorer/cc-cellar-explorer.client.js';
import '../cc-smart-container/cc-smart-container.js';
import { CcCellarNavigateToHomeEvent } from './cc-cellar-object-list.events.js';
import './cc-cellar-object-list.js';

/**
 * @import { CcCellarObjectList } from './cc-cellar-object-list.js'
 * @import { CellarObjectListState, CellarObjectListStateLoaded, CellarObjectState, CellarFileState, CellarFileDetailsState, CellarDirectoryCreateFormState } from './cc-cellar-object-list.types.js'
 * @import { CellarExplorerClient } from '../cc-cellar-explorer/cc-cellar-explorer.client.js'
 * @import { CellarDirectory } from '../cc-cellar-explorer/cc-cellar-explorer.client.types.js'
 * @import { UpdateCallback } from '../common.types.js'
 * @import { OnEventCallback } from '../../lib/smart/smart-component.types.js'
 */

const DIRECTORY_INVALID_CHARS = /[/\\{}^%`\]">[~<#|\u0080-\u00FF]/;

export class ObjectListController {
  /** @type {CellarExplorerClient} */
  #cellarClient;
  /** @type {() => CcCellarObjectList} */
  #getComponent;
  /** @type {UpdateCallback<CellarObjectListState>} */
  #updateState;
  /** @type {string} */
  #bucketName;
  /** @type {Array<string>} */
  #path;
  /** @type {Array<CellarObjectState>} */
  #objects;
  /** @type {string} */
  #filter;
  /** @type {string|null} */
  #nextCursor;
  /** @type {string|null} */
  #currentCursor;
  /** @type {Array<string>} */
  #previousPages = [];
  /** @type {Abortable} */
  #abortable;
  /** @type {NodeJS.Timeout} */
  #signedUrlInterval;

  /**
   * @param {CellarExplorerClient} cellarClient
   * @param {() => CcCellarObjectList} getComponent
   * @param {UpdateCallback<CellarObjectListState>} updateState
   */
  constructor(cellarClient, getComponent, updateState) {
    this.#cellarClient = cellarClient;
    this.#getComponent = getComponent;
    this.#updateState = updateState;
    this.#objects = [];
    this.#filter = '';
    this.#path = [];
    this.#abortable = new Abortable();
  }

  /**
   * @param {OnEventCallback} onEvent
   */
  init(onEvent) {
    onEvent('cc-cellar-object-filter', (filter) => {
      this.filter(filter);
    });

    onEvent('cc-cellar-navigate-to-path', (path) => {
      this.navigateTo(path);
    });

    onEvent('cc-cellar-navigate-to-previous-page', () => {
      this.previousPage();
    });

    onEvent('cc-cellar-navigate-to-next-page', () => {
      this.nextPage();
    });

    onEvent('cc-cellar-object-show', (objectKey) => {
      this.showDetails(objectKey);
    });

    onEvent('cc-cellar-object-hide', () => {
      this.hideDetails();
    });

    onEvent('cc-cellar-object-delete', (objectKey) => {
      this.deleteObject(objectKey);
    });

    onEvent('cc-cellar-object-create-directory', (directoryName) => {
      this.createDirectory(directoryName);
    });

    onEvent('cc-cellar-object-download', (objectKey) => {
      this.download(objectKey);
    });

    onEvent('cc-cellar-object-upload', (file) => {
      this.uploadObject(file);
    });
  }

  abort() {
    this.#clearSignedUrlInterval();
    this.#abortable.abort();
  }

  /**
   * @param {string} bucketName
   * @returns {Promise<void>}
   */
  async changeBucket(bucketName) {
    this.#bucketName = bucketName;
    this.#path = [];
    this.#nextCursor = null;
    this.#currentCursor = null;
    this.#previousPages = [];

    this.#updateState({ type: 'loading', bucketName: this.#bucketName, path: this.#path });
    await this.#fetchObjects();
  }

  /**
   * @param {string} filter
   */
  async filter(filter) {
    this.#filter = filter;
    this.#nextCursor = null;
    this.#currentCursor = null;
    this.#previousPages = [];
    this.#updateState({ type: 'filtering', bucketName: this.#bucketName, path: this.#path, filter: this.#filter });

    await this.#fetchObjects();
  }

  /**
   * @param {Array<string>} path
   */
  async navigateTo(path) {
    this.#path = path;

    this.#updateState({ type: 'loading', bucketName: this.#bucketName, path: this.#path });
    await this.#fetchObjects();
  }

  async previousPage() {
    if (this.#previousPages.length === 0) {
      return;
    }
    this.#currentCursor = this.#previousPages.pop();

    this.#updateState({ type: 'loading', bucketName: this.#bucketName, path: this.#path });
    await this.#fetchObjects();
  }

  async nextPage() {
    if (this.#nextCursor == null) {
      return;
    }

    this.#previousPages.push(this.#currentCursor);
    this.#currentCursor = this.#nextCursor;

    this.#updateState({ type: 'loading', bucketName: this.#bucketName, path: this.#path });
    await this.#fetchObjects();
  }

  /**
   * @param {string} objectKey
   */
  async showDetails(objectKey) {
    this.#updateFileState(objectKey, { state: 'fetching' });
    try {
      const bucketDetails = await this.#cellarClient.getObject(this.#bucketName, objectKey);

      const refreshSignedUrl = async () => {
        const signedUrl = await this.#cellarClient.getObjectSignedUrl(this.#bucketName, objectKey, 30);
        this.#updateState(
          /** @param {CellarObjectListStateLoaded} state */ (state) => {
            state.details = { state: 'idle', ...bucketDetails, signedUrl: signedUrl.url };
          },
        );
      };

      await refreshSignedUrl();
      this.#signedUrlInterval = setInterval(refreshSignedUrl, 20_000);
    } catch (error) {
      this.#handleErrorOnObject({
        error,
        objectKey,
        orElse: () => notifyError(i18n('cc-cellar-object-list.error.object-fetch-failed', { objectKey })),
      });
    } finally {
      this.#updateFileState(objectKey, { state: 'idle' });
    }
  }

  hideDetails() {
    this.#clearSignedUrlInterval();
    this.#updateDetails(null);
  }

  /**
   * @param {string} objectKey
   * @returns {Promise<void>}
   */
  async deleteObject(objectKey) {
    this.#updateDetails({ state: 'deleting' });

    try {
      await this.#cellarClient.deleteObject(this.#bucketName, objectKey);
      notifySuccess(i18n('cc-cellar-object-list.success.object-deleted', { objectKey }));
      this.#removeObject(objectKey);
    } catch (error) {
      this.#handleErrorOnObject({
        error,
        objectKey,
        orElse: () => notifyError(i18n('cc-cellar-object-list.error.object-deletion-failed', { objectKey })),
        deleteMode: true,
      });
    } finally {
      // in any case, we need to hide the details to make the toast visible
      this.hideDetails();
    }
  }

  /**
   * @param {string} objectKey
   * @returns {Promise<void>}
   */
  async download(objectKey) {
    this.#updateDetails({ state: 'downloading' });
    try {
      const signedUrl = await this.#cellarClient.getObjectSignedUrl(this.#bucketName, objectKey);

      const element = document.createElement('a');
      element.setAttribute('href', signedUrl.url);
      element.setAttribute('target', '_blank');
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      this.#handleErrorOnObject({
        error,
        objectKey,
        orElse: () => notifyError(i18n('cc-cellar-object-list.error.object-download-failed', { objectKey })),
      });
    } finally {
      this.#updateDetails({ state: 'idle' });
    }
  }

  async #fetchObjects() {
    try {
      const response = await this.#abortable.run(() =>
        this.#cellarClient.listObjects(this.#bucketName, this.#path, {
          cursor: this.#currentCursor,
          filter: this.#filter,
        }),
      );
      this.#objects = [...response.directories, ...response.content].map((object) =>
        object.type === 'file' ? /** @type {CellarFileState} */ ({ ...object, state: 'idle' }) : object,
      );
      this.#nextCursor = response.cursor;
      this.#updateState({
        type: 'loaded',
        bucketName: this.#bucketName,
        path: this.#path,
        filter: this.#filter,
        objects: this.#objects,
        hasNext: this.#nextCursor != null,
        hasPrevious: this.#currentCursor != null,
      });
      this.#getComponent().focusFirstCell();
    } catch (error) {
      console.log(error);
      this.#updateState({ type: 'error', bucketName: this.#bucketName, path: this.#path });
    }
  }

  /**
   * @param {string} objectKey
   */
  #removeObject(objectKey) {
    this.#objects = this.#objects.filter((object) => object.key !== objectKey);
    this.#updateState(
      /** @param {CellarObjectListStateLoaded} state */ (state) => {
        state.objects = this.#objects;
      },
    );
  }

  /**
   * @param {string} key
   * @param {Partial<CellarFileState>} newState
   */
  #updateFileState(key, newState) {
    this.#objects = this.#objects.map((object) =>
      object.key === key && object.type === 'file' ? { ...object, ...newState } : object,
    );
    this.#updateState(
      /** @param {CellarObjectListStateLoaded} state */ (state) => {
        state.objects = this.#objects;
      },
    );
  }

  /**
   * @param {Partial<CellarFileDetailsState>} newState
   */
  #updateDetails(newState) {
    this.#updateState(
      /** @param {CellarObjectListStateLoaded} state */ (state) => {
        if (newState == null) {
          state.details = null;
        } else {
          state.details = { ...state.details, ...newState };
        }
      },
    );
  }

  /**
   * @param {object} params
   * @param {unknown} params.error
   * @param {string} params.objectKey
   * @param {function} params.orElse
   * @param {boolean} [params.deleteMode]
   */
  #handleErrorOnObject({ error, objectKey, orElse, deleteMode = false }) {
    if (isCellarExplorerErrorWithCode(error, 'clever.cellar.bucket-not-found')) {
      notifyError(i18n('cc-cellar-object-list.error.bucket-not-found', { bucketName: this.#bucketName }));
      this.#getComponent().dispatchEvent(new CcCellarNavigateToHomeEvent());
    } else if (isCellarExplorerErrorWithCode(error, 'clever.cellar.object-not-found')) {
      if (deleteMode) {
        notifySuccess(i18n('cc-cellar-object-list.success.object-already-deleted', { objectKey }));
      } else {
        notifyError(i18n('cc-cellar-object-list.error.object-not-found', { objectKey }));
      }
      this.#removeObject(objectKey);
    } else {
      console.log(error);
      orElse();
    }
  }

  #clearSignedUrlInterval() {
    if (this.#signedUrlInterval != null) {
      clearInterval(this.#signedUrlInterval);
      this.#signedUrlInterval = null;
    }
  }

  /**
   * @param {string} directoryName
   */
  async createDirectory(directoryName) {
    this.#updateCreateForm({ type: 'creating', directoryName, error: null });

    if (DIRECTORY_INVALID_CHARS.test(directoryName)) {
      this.#updateCreateForm({ type: 'idle', directoryName, error: 'directory-name-invalid' });
      return;
    }

    const alreadyExists = this.#objects.some((obj) => obj.type === 'directory' && obj.name === directoryName);

    if (alreadyExists) {
      this.#updateCreateForm({ type: 'idle', directoryName, error: 'directory-already-exists' });
      return;
    }

    /** @type {CellarDirectory} */
    const newDir = {
      type: 'directory',
      key: [...this.#path, directoryName].join('/'),
      name: directoryName,
      volatile: true,
    };

    this.#objects = [...this.#objects, newDir].sort((a, b) => {
      if (a.volatile && !b.volatile) {
        return -1;
      }
      if (!a.volatile && b.volatile) {
        return 1;
      }
      if (a.type === 'directory' && b.type === 'file') {
        return -1;
      }
      if (a.type === 'file' && b.type === 'directory') {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });

    this.#updateState(
      /** @param {CellarObjectListStateLoaded} state */ (state) => {
        state.objects = this.#objects;
        state.createDirectoryForm = null;
      },
    );
    notifySuccess(i18n('cc-cellar-object-list.success.directory-created', { directoryName }));
  }

  /**
   * @param {File} file
   * @returns {Promise<void>}
   */
  async uploadObject(file) {
    const pathAtUploadStart = this.#path;
    const objectKey = pathToString(this.#path) + file.name;
    this.#updateUploadState({ type: 'uploading' });

    try {
      await this.#cellarClient.uploadObject(this.#bucketName, objectKey, file);
      notifySuccess(i18n('cc-cellar-object-list.success.object-uploaded', { objectKey }));

      if (this.#path === pathAtUploadStart) {
        /** @type {CellarFileState} */
        const newFile = {
          type: 'file',
          key: objectKey,
          name: file.name,
          updatedAt: new Date().toISOString(),
          contentLength: file.size,
          volatile: true,
          state: 'idle',
        };
        this.#objects = [newFile, ...this.#objects.filter((obj) => obj.key !== objectKey)];
        this.#updateState(
          /** @param {CellarObjectListStateLoaded} state */ (state) => {
            state.objects = this.#objects;
            state.uploadState = { type: 'idle' };
          },
        );
      }
    } catch (error) {
      if (this.#path === pathAtUploadStart) {
        this.#handleErrorOnObject({
          error,
          objectKey,
          orElse: () => notifyError(i18n('cc-cellar-object-list.error.object-upload-failed', { objectKey })),
        });
      } else {
        notifyError(i18n('cc-cellar-object-list.error.object-upload-failed', { objectKey }));
      }
    } finally {
      if (this.#path === pathAtUploadStart) {
        this.#updateUploadState({ type: 'idle' });
      }
    }
  }

  /**
   * @param {import('./cc-cellar-object-list.types.js').CellarObjectUploadState} newState
   */
  #updateUploadState(newState) {
    this.#updateState(
      /** @param {CellarObjectListStateLoaded} state */ (state) => {
        state.uploadState = newState;
      },
    );
  }

  /**
   * @param {Partial<CellarDirectoryCreateFormState>|null} newState
   */
  #updateCreateForm(newState) {
    this.#updateState(
      /** @param {CellarObjectListStateLoaded} state */ (state) => {
        if (newState == null) {
          state.createDirectoryForm = null;
        } else {
          state.createDirectoryForm = { ...state.createDirectoryForm, ...newState };
        }
      },
    );
  }
}
